import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest, formatDate } from '../../utils';
import './MemberHome.css';

/* A coach's own project claims — "I did a project of X AED with client Y". Bravos reviews them and
   follows up on the referral fee. Backend: GET/POST /api/bravos/claims. */
const MyClaims = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [claims, setClaims] = useState(null);
  const [clientName, setClientName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/claims')
      .then((res) => setClaims((res && res.claims) || []))
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const file = (e) => {
    e.preventDefault();
    if (!clientName.trim()) { setNotice(t('claimClientRequired')); return; }
    setSaving(true);
    setNotice(null);
    apiRequest('POST', '/bravos/claims', {
      client_name: clientName.trim(),
      amount: amount.trim(),
      currency: currency.trim() || 'AED',
      description: description.trim(),
    })
      .then(() => {
        setClientName(''); setAmount(''); setDescription('');
        setNotice(t('claimFiled'));
        load();
      })
      .catch(() => setNotice(t('saveFailed')))
      .finally(() => setSaving(false));
  };

  const statusClass = (s) =>
    s === 'confirmed' ? 'bravos-cta__tag--done' : (s === 'rejected' ? 'bravos-claim__tag--rej' : '');

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div>
            <Title my="0" className="mt-1">{t('myClaims')}</Title>
            <p className="bravos-brand__name">{t('myClaimsNote')}</p>
          </div>
          <button type="button" className="bravos-wallet__link bravos-members__back"
                  onClick={() => navigate('/member-home')}>
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      <Card>
        <Title my="1" className="mt-2">
          <Icon name="plus" theme="primary" className="me-2" />{t('fileAClaim')}
        </Title>
        <form onSubmit={file}>
          <label className="bravos-form__label">{t('clientName')}</label>
          <input className="bravos-form__input" type="text" value={clientName}
                 placeholder={t('clientNamePlaceholder')}
                 onChange={(e) => setClientName(e.target.value)} />
          <div className="bravos-claim__row">
            <div style={{ flex: 2 }}>
              <label className="bravos-form__label">{t('projectValue')}</label>
              <input className="bravos-form__input" type="number" min="0" value={amount}
                     placeholder="0" onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="bravos-form__label">{t('currency')}</label>
              <input className="bravos-form__input" type="text" value={currency}
                     maxLength={10} onChange={(e) => setCurrency(e.target.value)} />
            </div>
          </div>
          <label className="bravos-form__label">{t('claimDescription')}</label>
          <textarea className="bravos-form__input" rows={3} value={description}
                    onChange={(e) => setDescription(e.target.value)} />
          {notice && <p className="bravos-cal__notice mt-2">{notice}</p>}
          <button type="submit" className="bravos-event__claim mt-2" disabled={saving}>
            {saving ? '…' : t('submitClaim')}
          </button>
        </form>
      </Card>

      {claims === null ? (
        <Card><span className="auth__spinner" /></Card>
      ) : claims.length > 0 && (
        <Card>
          <Title my="1" className="mt-2">{t('yourClaims')}</Title>
          {claims.map((c) => (
            <div key={c.id} className="bravos-member">
              <div className="bravos-member__row">
                <span className="bravos-member__body">
                  <span className="bravos-member__name">{c.client_name}</span>
                  <span className="bravos-member__email">
                    {c.amount != null ? `${Number(c.amount).toLocaleString()} ${c.currency || ''}` : ''}
                    {c.created_at ? ` · ${formatDate(c.created_at)}` : ''}
                  </span>
                </span>
                <span className={`bravos-cta__tag ${statusClass(c.status)}`}>
                  {t(`claimStatus_${c.status}`)}
                </span>
              </div>
            </div>
          ))}
        </Card>
      )}
    </Base>
  );
};

export default MyClaims;
